import Button, { type ButtonProps } from "./Button";
import ButtonContainer from "./ButtonContainer";

export type SemanticButtonProps = Omit<ButtonProps, "variant">;

export interface SemanticButtonsProps {
  /**
   * Primary button(s) — pass a single config or an array for multiple primary buttons
   */
  primary: SemanticButtonProps | SemanticButtonProps[];
  /**
   * Secondary button props (optional, after primary buttons)
   */
  secondary?: SemanticButtonProps;
  /**
   * Tertiary button props (optional, last position)
   */
  tertiary?: SemanticButtonProps;
  /**
   * Horizontal alignment of buttons. Use "end" for right-aligned layouts (with
   * order tertiary, secondary, primary, e.g. Cancel left of Delete). Currently
   * used in dialogs; also suitable for add buttons in page headers or wizard
   * proceed steps. This pattern is followed for example by GitHub (dialogs
   * use right aligned buttons and full page forms use left aligned buttons).
   */
  align?: "start" | "end";
  /**
   * Additional className for the container
   */
  className?: string;
}

/**
 * SemanticButtons component that displays buttons in a horizontal layout
 *
 * - Primary button(s) are rendered first (supports one or multiple)
 * - Secondary button (if provided) follows the primary buttons
 * - Tertiary button (if provided) is rendered last
 * - Container is full width with buttons aligned per align prop (default: left)
 * - When align="end", button order is reversed (secondary/tertiary before primary)
 */
export default function SemanticButtons({
  primary,
  secondary,
  tertiary,
  align = "start",
  className,
}: SemanticButtonsProps) {
  const primaryButtons = Array.isArray(primary) ? primary : [primary];

  const buttonProps: ButtonProps[] = [
    ...primaryButtons.map((props, index) => ({
      key: `primary-${index}` as const,
      variant: "primary" as const,
      ...props,
    })),
    ...(secondary
      ? [
          {
            key: `secondary` as const,
            variant: "secondary" as const,
            ...secondary,
          },
        ]
      : []),
    ...(tertiary
      ? [
          {
            key: "tertiary" as const,
            variant: "tertiary" as const,
            ...tertiary,
          },
        ]
      : []),
  ];

  const orderedButtons =
    align === "end" ? [...buttonProps].reverse() : buttonProps;

  return (
    <ButtonContainer align={align} className={className}>
      {orderedButtons.map(({ key, ...props }) => (
        <Button key={key} {...props} />
      ))}
    </ButtonContainer>
  );
}
